import { useLbeAuth } from '@laborelec/lbe-react/auth';
import { useCallback, useMemo } from 'react';

export type AppPermission = 'read' | 'create' | 'update' | 'delete' | 'unrestricted_nda' | 'export';

/** The scope whose roles are technodb's permissions (see `api.getMeRoles`). */
export const TECHNODB_SCOPE = 'technodb';

interface AuthContextValue {
  /** Display name from Okta ID token */
  name: string;
  /** Email from the Okta ID token, or else from the backend /me */
  email: string;
  /** Permissions from backend /me */
  permissions: AppPermission[];
  /** Check if the user has a specific permission */
  can: (permission: AppPermission) => boolean;
  /** True while permissions are still being fetched from backend */
  isLoading: boolean;
  /** Sign the user out */
  logout: () => Promise<void>;
}

/** The signed-in user and their technodb permissions. Must be used inside LbeAuthProvider. */
export const useAuth = (): AuthContextValue => {
  const { name, email, roles, isLoading, logout } = useLbeAuth();
  const permissions = roles as AppPermission[];
  const can = useCallback((permission: AppPermission) => permissions.includes(permission), [permissions]);

  return useMemo(
    () => ({ name, email, permissions, can, isLoading, logout }),
    [name, email, permissions, can, isLoading, logout],
  );
};
